/**
 * QdsHierarchicalMenu Web Component
 * 
 * A customizable hierarchical menu that organizes items based on specified attributes.
 * The component displays a tree structure that can be expanded/collapsed by the user.
 * 
 * @element qds-hierarchical-menu
 * 
 * @attr {String} data - JSON string containing menu items array
 * @attr {String} icon-root - Base URL for relative icon paths
 * @attr {String} icon-color - Color for the menu toggle icon
 * @attr {Boolean} use-icons - Whether to display icons next to menu items (defaults to true)
 * 
 * @csspart menu-button - The menu toggle button
 * @csspart menu-container - The container for the menu items
 * 
 * @fires toggle - When the menu is toggled open/closed
 * @fires nodeToggle - When a menu node is expanded/collapsed
 */

import { DEFAULT_ASSET_ROOT, QdsBaseMenu } from '../base-menu/qds-base-menu.js';
import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';
import { hydrateQdsLucideIcons } from '../shared/icon-utils.js';
import { HierarchicalMenuItem } from '../../types/index.js';
const DEFAULT_MENU_DATA_URL = new URL('json/menu-data.json', DEFAULT_ASSET_ROOT).toString();

interface QdsGlobalConfig {
  menuData?: HierarchicalMenuItem[];
}

declare global {
  interface Window {
    QDS?: QdsGlobalConfig;
  }
}

let defaultMenuDataPromise: Promise<HierarchicalMenuItem[]> | null = null;

function getDefaultMenuData(): Promise<HierarchicalMenuItem[]> {
  const configuredMenuData = window.QDS?.menuData;

  if (Array.isArray(configuredMenuData)) {
    return Promise.resolve(configuredMenuData);
  }

  if (!defaultMenuDataPromise) {
    defaultMenuDataPromise = fetch(DEFAULT_MENU_DATA_URL)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Failed to load default menu data: ${response.status} ${response.statusText}`);
        }

        return response.json();
      })
      .then(data => {
        if (!Array.isArray(data)) {
          throw new Error('Default menu data must be an array');
        }

        return data as HierarchicalMenuItem[];
      })
      .catch(error => {
        defaultMenuDataPromise = null;
        throw error;
      });
  }

  return defaultMenuDataPromise;
}

interface GenerateLinkOptions {
  className?: string;
  additionalAttributes?: string;
  includeIcon?: boolean;
  wrapContent?: boolean;
}

interface NodeToggleEventDetail {
  node: HTMLElement;
  isExpanded: boolean;
  wasPreviouslyExpanded: boolean;
}

interface ToggleEventDetail {
  isOpen: boolean;
}

class QdsHierarchicalMenu extends HTMLElement {
  // Private properties
  private _baseMenu: QdsBaseMenu;
  private iconColor: string;
  private isOpen: boolean;
  private _documentClickHandler: ((event: MouseEvent) => void) | null;
  private _defaultDataLoadStarted: boolean;

  /**
   * Creates an instance of HierarchicalMenu
   */
  constructor() {
    super();

    // Create base menu instance
    this._baseMenu = new QdsBaseMenu();

    // Color for menu toggle icon
    this.iconColor = "var(--menu-toggle-color, var(--icon-color-primary, var(--theme-text-primary)))";

    // Is menu open
    this.isOpen = false;

    // Document click handler
    this._documentClickHandler = null;
    this._defaultDataLoadStarted = false;

    // Create shadow DOM
    this.attachShadow({ mode: "open" });

    // Initial render
    this.render();
  }

  static get observedAttributes(): string[] {
    return ["data", "icon-root", "icon-color", "use-icons"];
  }

  /**
   * Called when an observed attribute changes
   * 
   * @param name - Name of the attribute that changed
   * @param oldValue - Previous value of the attribute
   * @param newValue - New value of the attribute
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === "data") {
      try {
        const data = JSON.parse(newValue);
        this._baseMenu.setData(data);
        this.render();
      } catch (e) {
        console.error("Invalid JSON data:", e);
      }
    }

    if (name === "icon-root") {
      this._baseMenu.setIconRoot(newValue || '');
      this.render();
    }

    if (name === "icon-color") {
      this.iconColor = newValue || "var(--menu-toggle-color, var(--icon-color-primary, var(--theme-text-primary)))";
      this.render();
    }

    if (name === "use-icons") {
      // Check if the attribute is explicitly set to 'false'
      this._baseMenu.useIcons = !(newValue === 'false');

      if (this.hasAttribute('debug')) {
        console.log('use-icons attribute changed:', {
          newValue,
          useIcons: this._baseMenu.useIcons
        });
      }

      this.render();
    }
  }

  /**
   * Called when the element is inserted into the DOM
   */
  connectedCallback(): void {
    if (!this.isConnected) return;

    // Attributes may be set between construction and connection, so defer the
    // fallback until now. This also allows window.QDS.menuData to be supplied
    // before the component enters the document.
    if (!this.hasAttribute('data') && !this._defaultDataLoadStarted) {
      this._defaultDataLoadStarted = true;
      getDefaultMenuData()
        .then(data => {
          if (!this.hasAttribute('data')) {
            this._baseMenu.setData(data);
            this.render();
          }
        })
        .catch(error => console.error('Error reading JSON:', error));
    }

    this.render();

    // Set up click handler for document to close the menu when clicking outside
    this._documentClickHandler = (event: MouseEvent) => {
      // Only process if menu is open
      if (!this.isOpen) return;

      const target = event.target as Node;
      const path = event.composedPath();

      // Check if the click target is outside our component
      if (!this.contains(target) &&
        !path.includes(this) &&
        this.shadowRoot && !this.shadowRoot.contains(target as Node)) {
        this.closeMenu();
      }
    };

    document.addEventListener('click', this._documentClickHandler);

  }

  /**
   * Called when the element is removed from the DOM
   */
  disconnectedCallback(): void {
    // Remove document click listener when element is removed
    if (this._documentClickHandler) {
      document.removeEventListener('click', this._documentClickHandler);
      this._documentClickHandler = null;
    }

  }

  /**
   * Open the menu
   */
  private openMenu(): void {
    const menuContainer = this.shadowRoot?.querySelector(".menu-container") as HTMLElement | null;
    if (menuContainer) {
      menuContainer.style.display = "flex";
      this.isOpen = true;

      // Update ARIA attributes
      const menuButton = this.shadowRoot?.querySelector("#menu-toggle-button") as HTMLButtonElement | null;
      const menuContainer2 = this.shadowRoot?.querySelector("#hierarchical-menu-container") as HTMLElement | null;

      if (menuButton) {
        menuButton.setAttribute("aria-expanded", "true");
      }

      if (menuContainer2) {
        menuContainer2.setAttribute("aria-hidden", "false");

        // Update the visibility of recent items container based on search term
        const recentItemsContainer = this.shadowRoot?.querySelector(".recent-items-container") as HTMLElement | null;
        if (recentItemsContainer && this._baseMenu.searchTerm) {
          recentItemsContainer.style.display = 'none';
        }

        // Focus on search input after opening for better keyboard navigation
        setTimeout(() => {
          const searchInput = this.shadowRoot?.querySelector("#menu-search-input") as HTMLInputElement | null;
          if (searchInput) {
            searchInput.focus();
          }
        }, 50);
      }
    }
  }

  /**
   * Close the menu
   */
  private closeMenu(): void {
    const menuContainer = this.shadowRoot?.querySelector(".menu-container") as HTMLElement | null;
    if (menuContainer) {
      menuContainer.style.display = "none";
      this.isOpen = false;

      // Update ARIA attributes
      const menuButton = this.shadowRoot?.querySelector("#menu-toggle-button") as HTMLButtonElement | null;
      const menuContainer2 = this.shadowRoot?.querySelector("#hierarchical-menu-container") as HTMLElement | null;

      if (menuButton) {
        menuButton.setAttribute("aria-expanded", "false");
      }

      if (menuContainer2) {
        menuContainer2.setAttribute("aria-hidden", "true");
      }
    }
  }

  /**
   * Toggle the menu open/closed
   */
  private toggleMenu(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation(); // Prevent immediate bubbling
      event.preventDefault();  // Prevent default to better handle touch events
    }

    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent<ToggleEventDetail>('toggle', {
      detail: { isOpen: this.isOpen },
      bubbles: true,
      composed: true
    }));
  }

  /**
   * Toggles expansion of a menu node
   * 
   * @param event - Click event
   */
  private toggleNode(event: Event): void {
    const target = event.target as HTMLElement;

    // Find the clicked node or leaf element
    const toggleElement = target.closest('.node, .leaf') as HTMLElement | null;
    if (!toggleElement) return;

    // Find the closest expandable item that contains this toggle element
    const parentLi = toggleElement.closest(".expandable-item") as HTMLElement | null;

    if (parentLi) {
      // Toggle the expanded class
      const wasExpanded = parentLi.classList.contains("expanded");
      parentLi.classList.toggle("expanded");

      // Update folder icon text between + and -
      const folderIcon = toggleElement.querySelector(".folder-icon") as HTMLElement | null;
      if (folderIcon) {
        folderIcon.textContent = parentLi.classList.contains("expanded") ? "-" : "+";

        // Update aria attributes for accessibility
        toggleElement.setAttribute("aria-expanded", parentLi.classList.contains("expanded").toString());
      }

      // Debug log the toggle operation
      if (this.hasAttribute('debug')) {
        console.log("Toggle operation:", {
          element: parentLi,
          isExpanded: parentLi.classList.contains("expanded"),
          childItems: parentLi.querySelector('.sub-menu, .leaf-items')
        });
      }

      // Dispatch node toggle event
      this.dispatchEvent(new CustomEvent<NodeToggleEventDetail>('nodeToggle', {
        detail: {
          node: parentLi,
          isExpanded: parentLi.classList.contains("expanded"),
          wasPreviouslyExpanded: wasExpanded
        },
        bubbles: true,
        composed: true
      }));
    }

    // Prevent event from bubbling up to parent list items
    event.stopPropagation();
  }

  /**
   * Handle search input event
   * 
   * @param event - Input event
   */
  private handleSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    const searchTerm = target.value;

    // Use the base menu's search functionality
    this._baseMenu.handleSearch(searchTerm);

    // Update menu content instead of full re-render
    this.updateMenuContent();

    // Toggle visibility of recent items container based on search term
    const recentItemsContainer = this.shadowRoot?.querySelector(".recent-items-container") as HTMLElement | null;
    if (recentItemsContainer) {
      recentItemsContainer.style.display = searchTerm ? 'none' : 'block';
    }
  }

  /**
   * Update menu content based on current state (search mode or normal mode)
   */
  private updateMenuContent(): void {
    const menuContentContainer = this.shadowRoot?.querySelector('.menu-content') as HTMLElement | null;
    if (!menuContentContainer) return;

    if (this._baseMenu.isSearchMode) {
      menuContentContainer.innerHTML = `<ul class="menu search-results">${this.createSearchResults(this._baseMenu.searchResults)}</ul>`;
    } else {
      menuContentContainer.innerHTML = this.createMenuTree(this._baseMenu.menuData);
      // Re-attach event listeners after updating content
      this.attachMenuEventListeners();
    }
  }

  /**
   * Creates HTML markup for search results
   * 
   * @param results - Search results to display
   * @returns HTML string representing search results
   */
  private createSearchResults(results: HierarchicalMenuItem[]): string {
    if (!results.length) {
      return `<p class="no-results">No results found</p>`;
    }

    // Generate HTML for search results
    return results.map(item => `
      <li class="item search-result">
        <a href="${item.linkUrl}" target="${item.target || '_blank'}">
          ${this._baseMenu.useIcons && item.icon ? this._baseMenu.generateIconHtml(item) : ''}
          <div class="search-item-details">
            <div class="search-item-name">${item.name}</div>
            ${item.description ? `<div class="search-item-description">${item.description}</div>` : ''}
            ${item.businessFunction ? `<div class="search-item-function">${item.businessFunction}</div>` : ''}
          </div>
        </a>
      </li>
    `).join('');
  }

  /**
   * Creates HTML markup for the menu tree
   * 
   * @param menuItems - Hierarchical menu items to render
   * @returns HTML string representing the menu
   */
  private createMenuTree(menuItems: HierarchicalMenuItem[]): string {
    if (!menuItems || menuItems.length === 0) {
      return '';
    }

    /**
     * Recursively creates HTML for tree nodes
     * 
     * @param items - Items to render in the tree
     * @returns HTML string for the items
     */
    const createTree = (items: HierarchicalMenuItem[]): string => {
      return items.map(item => {
        // Check if this is a parent/category item with children
        if (this._baseMenu.hasChildren(item)) {
          // This is a branch node with children
          return `
            <li class="expandable-item">
              <span class="node" aria-expanded="false">
                ${item.name}<span class="folder-icon">+</span>
              </span>
              <ul class="sub-menu" role="group">${createTree(item.items || [])}</ul>
            </li>
          `;
        } else {
          // This is a leaf node (no children)
          return `
            <li class="item" title="${item.description || ''}">
              ${this._baseMenu.generateLinkHtml(item, {
            additionalAttributes: 'role="menuitem"'
          })}
            </li>`;
        }
      }).join("");
    };

    return `<ul class="menu" role="menu">${createTree(menuItems)}</ul>`;
  }

  /**
   * Creates HTML markup for recent items
   * 
   * @returns HTML string for recent items
   */
  private createRecentItemsHTML(): string {
    const recentItems = this._baseMenu.getRecentItems(4);

    if (!recentItems.length) {
      return '';
    }

    return recentItems.map(item => {
      const iconHtml = this._baseMenu.renderIconHtml(item);

      return `
        <a href="${item.linkUrl}" class="recent-item" title="${item.description || item.name}" target="${item.target || '_blank'}">
          ${iconHtml}
          <span class="recent-item-name">${item.name}</span>
        </a>
      `;
    }).join('');
  }

  /**
   * Attach event listeners to menu elements
   */
  private attachMenuEventListeners(): void {
    // Add keyboard navigation and touch support
    const menuContainer = this.shadowRoot?.querySelector(".menu-container") as HTMLElement | null;
    if (!menuContainer) return;

    // Custom property to track if event handlers are already attached
    const eventHandlersAttached = (menuContainer as any)._eventHandlersAttached;

    // Check if we've already set up the event listeners
    if (!eventHandlersAttached) {
      // Setup click handler for expanding/collapsing nodes
      menuContainer.addEventListener("click", (event: MouseEvent) => {
        // Prevent menu container clicks from propagating to document
        event.stopPropagation();

        // Debug: show what was clicked
        if (this.hasAttribute('debug')) {
          console.log("Click target:", event.target);
          console.log("Click target classList:", (event.target as HTMLElement).classList);
          console.log("Closest toggle element:", (event.target as HTMLElement).closest('.node, .leaf'));
        }

        // Check if we clicked on a node or leaf element
        const target = event.target as HTMLElement;
        const toggleElement = target.closest('.node, .leaf');
        if (toggleElement) {
          this.toggleNode(event);
        }
      });

      // Setup keyboard navigation
      menuContainer.addEventListener("keydown", (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          this.closeMenu();
          const menuButton = this.shadowRoot?.querySelector("#menu-toggle-button") as HTMLButtonElement | null;
          if (menuButton) menuButton.focus();
        } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          // Navigate between menu items
          const focusableElements = menuContainer.querySelectorAll('.node, .leaf, a, button, input');
          const focusableArray = Array.from(focusableElements);
          const currentElement = document.activeElement;
          const currentIndex = focusableArray.indexOf(currentElement as Element);

          if (currentIndex >= 0) {
            let nextIndex;
            if (event.key === "ArrowDown") {
              nextIndex = (currentIndex + 1) % focusableArray.length;
            } else {
              nextIndex = (currentIndex - 1 + focusableArray.length) % focusableArray.length;
            }
            (focusableArray[nextIndex] as HTMLElement).focus();
            event.preventDefault();
          }
        } else if (event.key === "Enter" || event.key === " ") {
          // Toggle node on Enter or Space
          const activeElement = document.activeElement as HTMLElement;
          if (activeElement && (activeElement.classList.contains('node') || activeElement.classList.contains('leaf'))) {
            // Create a minimal event-like object with the properties we need
            const fakeEvent = new Event('click', { bubbles: true });
            Object.defineProperty(fakeEvent, 'target', { value: activeElement });
            this.toggleNode(fakeEvent);
            event.preventDefault();
          }
        }
      });

      // Add touch support
      menuContainer.addEventListener("touchstart", (event: TouchEvent) => {
        // Debug: show touch event
        if (this.hasAttribute('debug')) {
          console.log("Touch target:", event.target);
        }
      }, { passive: true });

      // Mark as attached
      (menuContainer as any)._eventHandlersAttached = true;
    }
  }

  /**
   * Renders the component's HTML structure and attaches event listeners
   */
  private render(): void {
    if (!this.shadowRoot) return;

    const searchMask = 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z\'/%3E%3C/svg%3E")';

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --_menu-font-family: var(--qds-menu-font-family, var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif)));
          --_menu-font-size: var(--qds-menu-font-size, var(--theme-font-size-sm, 14px));
          --_menu-meta-font-size: var(--qds-menu-meta-font-size, var(--theme-font-size-xs, 12px));
          --_menu-label-font-weight: var(--qds-menu-label-font-weight, var(--theme-font-weight-semibold, 600));
          --_menu-button-size: var(--qds-menu-button-size, 44px);
          --_menu-button-radius: var(--qds-menu-button-radius, var(--theme-radius-sm, 4px));
          --_menu-icon-size: var(--qds-menu-icon-size, 24px);
          --_menu-panel-width: var(--qds-menu-panel-width, 464px);
          --_menu-panel-min-height: var(--qds-menu-panel-min-height, 640px);
          --_menu-panel-max-height: var(--qds-menu-panel-max-height, 95vh);
          --_menu-panel-radius: var(--qds-menu-panel-radius, 24px);
          --_menu-mobile-panel-radius: var(--qds-menu-mobile-panel-radius, calc(var(--theme-radius-lg, 12px) + var(--theme-space-1, 4px)));
          --_menu-panel-offset: var(--qds-menu-panel-offset, var(--theme-space-2, 8px));
          --_menu-section-padding: var(--qds-menu-section-padding, var(--theme-space-4, 16px));
          --_menu-surface-padding: var(--qds-menu-surface-padding, var(--theme-space-3, 12px));
          --_menu-search-padding-block: var(--qds-menu-search-padding-block, var(--theme-space-4, 16px));
          --_menu-search-padding-inline: var(--qds-menu-search-padding-inline, var(--theme-space-3, 12px));
          --_menu-search-leading-padding: var(--qds-menu-search-leading-padding, calc(var(--theme-space-6, 24px) + var(--theme-space-5, 20px)));
          --_menu-search-icon-size: var(--qds-menu-search-icon-size, 24px);
          --_menu-row-radius: var(--qds-menu-row-radius, var(--theme-radius-sm, 4px));
          --_menu-card-radius: var(--qds-menu-card-radius, var(--theme-radius-md, 8px));
          --_menu-item-padding-block: var(--qds-menu-item-padding-block, var(--theme-space-2, 8px));
          --_menu-item-padding-inline: var(--qds-menu-item-padding-inline, var(--theme-space-3, 12px));
          --_menu-item-gap: var(--qds-menu-item-gap, var(--theme-space-2, 8px));
          --_menu-item-margin-inline: var(--qds-menu-item-margin-inline, var(--theme-space-4, 16px));
          --_menu-item-margin-bottom: var(--qds-menu-item-margin-bottom, var(--theme-space-2, 8px));
          --_menu-tree-indent: var(--qds-menu-tree-indent, var(--theme-space-5, 20px));
          --_menu-scrollbar-size: var(--qds-menu-scrollbar-size, var(--theme-space-2, 8px));
          --_menu-folder-icon-width: var(--qds-menu-folder-icon-width, var(--theme-space-4, 16px));
          --_menu-recent-name-font-size: var(--qds-menu-recent-name-font-size, var(--theme-font-size-xs, 12px));
          --_menu-text-color: var(--menu-text-color, var(--theme-text-primary));
          --_menu-subtle-text-color: var(--menu-subtle-text-color, var(--theme-text-secondary));
          --_menu-muted-text-color: var(--menu-muted-text-color, var(--theme-text-tertiary));
          --_menu-surface-color: var(--menu-background, var(--theme-background-primary));
          --_menu-panel-color: var(--menu-elevated-background, var(--theme-background-primary));
          --_menu-hover-color: var(--menu-hover-background, var(--theme-background-tertiary));
          --_menu-border-color: var(--menu-border-color, var(--theme-border-primary));
          --_menu-border-strong-color: var(--menu-border-strong-color, var(--theme-border-strong));
          --_menu-panel-shadow: var(--menu-panel-shadow, var(--theme-shadow-md));
          --_menu-surface-shadow: var(--menu-shadow, var(--theme-shadow-sm));
          --_menu-button-hover-color: var(--menu-button-hover-background, var(--button-hover-bg));
          display: inline-block;
          position: relative;
          font-family: var(--_menu-font-family);
          font-size: var(--_menu-font-size);
          color: var(--_menu-text-color);
          min-height: var(--_menu-button-size);
          min-width: var(--_menu-button-size);
        }
        
        *, *::before, *::after {
          box-sizing: border-box;
        }

        .menu-button {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          background: transparent;
          border: none;
          color: ${this.iconColor};
          width: var(--_menu-button-size);
          height: var(--_menu-button-size);
          padding: 0;
          min-width: var(--_menu-button-size);
          min-height: var(--_menu-button-size);
          border-radius: var(--_menu-button-radius);
          touch-action: manipulation;
        }

        .menu-button svg {
          width: var(--_menu-icon-size);
          height: var(--_menu-icon-size);
          fill: currentColor;
        }

        .menu-button:hover,
        .menu-button:focus-visible {
          background-color: var(--_menu-button-hover-color);
        }

        .menu-button:focus-visible {
          outline: 2px solid var(--theme-accent-primary);
          outline-offset: 2px;
        }

        .menu-container {
          position: absolute;
          top: 100%;
          left: 0;
          width: var(--_menu-panel-width);
          max-width: calc(100vw - 24px);
          max-height: var(--_menu-panel-max-height);
          min-height: var(--_menu-panel-min-height);
          font-size: var(--_menu-font-size);
          background: var(--_menu-panel-color);
          border-radius: var(--_menu-panel-radius);
          box-shadow: var(--_menu-panel-shadow);
          border: 1px solid var(--_menu-border-color);
          overflow: hidden;
          scrollbar-width: thin;
          scrollbar-color: var(--_menu-border-strong-color) transparent;
          display: none;
          flex-direction: column;
          margin-top: var(--_menu-panel-offset);
          z-index: 1010;
        }
        
        .search-container {
          padding: var(--_menu-search-padding-block) var(--_menu-search-padding-inline);
          border-bottom: 1px solid var(--_menu-border-color);
          position: sticky;
          top: 0;
          background: var(--_menu-panel-color);
          z-index: 2;
          position: relative;
        }
        
        .search-container::before {
          content: '';
          position: absolute;
          left: calc(var(--_menu-search-padding-inline) + var(--theme-space-2, 8px));
          top: 50%;
          transform: translateY(-50%);
          width: var(--_menu-search-icon-size);
          height: var(--_menu-search-icon-size);
          background-color: var(--_menu-muted-text-color);
          -webkit-mask-image: ${searchMask};
          -webkit-mask-repeat: no-repeat;
          -webkit-mask-size: contain;
          mask-image: ${searchMask};
          mask-repeat: no-repeat;
          mask-size: contain;
          z-index: 3;
          pointer-events: none;
        }
        
        .search-input {
          width: 100%;
          padding: var(--theme-space-2, 8px) var(--theme-space-3, 12px) var(--theme-space-2, 8px) var(--_menu-search-leading-padding);
          border: none;
          background-color: transparent;
          color: var(--_menu-text-color);
          font-size: var(--_menu-font-size);
          font-family: var(--_menu-font-family);
          box-shadow: none;
        }

        .search-input::placeholder {
          color: var(--_menu-muted-text-color);
        }
        
        .recent-items-container {
          padding: var(--_menu-section-padding);
          border-bottom: 1px solid var(--_menu-border-color);
        }
        
        .recent-items {
          display: flex;
          justify-content: space-between;
          gap: var(--theme-space-2, 8px);
        }
        
        .recent-item {
          width: calc(25% - 8px);
          aspect-ratio: 1 / 1;
          border-radius: var(--_menu-card-radius);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background-color: var(--_menu-surface-color);
          cursor: pointer;
          box-shadow: var(--_menu-surface-shadow);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          text-decoration: none;
          color: inherit;
          padding: var(--theme-space-2, 8px);
          position: relative;
          border: 1px solid var(--_menu-border-color);
        }
        
        .recent-item:hover {
          transform: translateY(-2px);
          box-shadow: var(--_menu-panel-shadow);
        }
        
        .recent-item img,
        .recent-item .icon {
          width: 60%;
          height: 60%;
          object-fit: contain;
          margin-bottom: 6px;
        }
        
        .recent-item-name {
          font-size: var(--_menu-recent-name-font-size);
          text-align: center;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-weight: var(--_menu-label-font-weight);
        }
        
        .menu-content {
          overflow-y: auto;
          overflow-x: hidden;
          position: relative;
          scrollbar-width: thin;
          scrollbar-color: var(--_menu-border-strong-color) transparent;
        }
        
        .menu-content::-webkit-scrollbar {
          width: var(--_menu-scrollbar-size);
        }
        
        .menu-content::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .menu-content::-webkit-scrollbar-thumb {
          background-color: var(--_menu-border-strong-color);
          border-radius: var(--_menu-button-radius);
          border: 2px solid transparent;
        }
        
        .menu-content::-webkit-scrollbar-thumb:hover {
          background-color: var(--_menu-muted-text-color);
        }

        .menu {
          list-style: none;
          margin: 0;
          padding: var(--_menu-surface-padding);
        }

        .menu > li {
          padding: var(--_menu-item-padding-block) var(--theme-space-1, 4px);
          cursor: pointer;
          display: block;
          margin: 0 var(--_menu-item-margin-inline);
          border-bottom: 1px solid var(--_menu-border-color);
          margin-bottom: var(--_menu-item-margin-bottom);
          padding-bottom: var(--_menu-surface-padding);
        }
        
        .menu > li:last-child {
          border-bottom: none;
        }
        
        .menu li:not(.menu > li) {
          padding: var(--theme-space-1, 4px);
          cursor: pointer;
          display: block;
        }

        .menu li span.node, .menu li span.leaf {
          font-weight: var(--_menu-label-font-weight);
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--_menu-item-gap);
          padding: calc(var(--_menu-item-padding-block) - var(--theme-space-1, 4px) / 2) var(--_menu-item-padding-inline);
          border-radius: var(--_menu-row-radius);
          position: relative;
        }

        .menu li span.node:hover, .menu li span.leaf:hover {
          background: transparent;
        }

        .menu li .sub-menu, .menu li .leaf-items {
          list-style: none;
          display: none;
          padding-left: var(--_menu-tree-indent);
          margin-top: var(--theme-space-1, 4px);
          width: calc(100% - var(--_menu-tree-indent));
          box-sizing: border-box;
        }
        
        .menu li .sub-menu > li, .menu li .leaf-items > li {
          margin: 0;
          border-bottom: none;
        }

        .menu li.expanded > .sub-menu, 
        .menu li.expanded > .leaf-items {
          display: block;
        }
        
        .expandable-item {
          position: relative;
        }
        
        .folder-icon {
          display: inline-block;
          width: var(--_menu-folder-icon-width);
          text-align: center;
          font-weight: var(--_menu-label-font-weight);
          margin-left: var(--_menu-item-gap);
          position: absolute;
          right: var(--theme-space-1, 4px);
        }
        
        .menu li.expanded > span > .folder-icon {
          content: "-";
        }

        .menu li a {
          text-decoration: none;
          color: var(--_menu-text-color);
          display: flex;
          align-items: center;
          gap: var(--_menu-item-gap);
          padding: calc(var(--_menu-item-padding-block) - var(--theme-space-1, 4px) / 2) var(--_menu-item-padding-inline);
          border-radius: var(--_menu-row-radius);
        }

        .menu li.item {
          margin-bottom: var(--theme-space-1, 4px);
        }

        .menu li a .icon {
          width: var(--_menu-icon-size);
          height: var(--_menu-icon-size);
          min-width: var(--_menu-icon-size);
        }

        .menu li a:hover {
          background-color: var(--_menu-hover-color);
        }
        
        .search-item-details {
          display: flex;
          flex-direction: column;
        }
        
        .search-item-name {
          font-weight: var(--_menu-label-font-weight);
        }
        
        .search-item-description,
        .search-item-function {
          font-size: var(--_menu-meta-font-size);
          color: var(--_menu-subtle-text-color);
        }
        
        .search-item-description {
          margin-top: var(--theme-space-1, 4px);
          font-style: italic;
        }
        
        .no-results {
          padding: var(--_menu-surface-padding);
          color: var(--_menu-muted-text-color);
          font-style: italic;
          text-align: center;
        }

        @media (max-width: 480px) {
          :host {
            max-width: 100%;
          }

          .menu-container {
            width: calc(100vw - 24px);
            max-width: calc(100vw - 24px);
            min-height: 0;
            max-height: calc(100vh - 96px);
            border-radius: var(--_menu-mobile-panel-radius);
            left: 0;
            right: auto;
          }
        }
      </style>

      <button id="menu-toggle-button" class="menu-button" part="menu-button" aria-label="Toggle menu" aria-expanded="${this.isOpen ? 'true' : 'false'}" aria-controls="hierarchical-menu-container">
        <svg viewBox="0 0 24 24">
          <path d="M6,8c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM12,20c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM6,20c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM6,14c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM12,14c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM16,6c0,1.1 0.9,2 2,2s2,-0.9 2,-2 -0.9,-2 -2,-2 -2,0.9 -2,2zM12,8c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM18,14c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2zM18,20c1.1,0 2,-0.9 2,-2s-0.9,-2 -2,-2 -2,0.9 -2,2 0.9,2 2,2z"></path>
        </svg>
      </button>
      <div id="hierarchical-menu-container" class="menu-container" part="menu-container" role="menu" aria-hidden="${this.isOpen ? 'false' : 'true'}">
          <div class="search-container">
            <input type="text" 
                  id="menu-search-input"
                  class="search-input" 
                  placeholder="Search menu..." 
                  value="${this._baseMenu.searchTerm}"
                  aria-label="Search menu">
        </div>
        <div class="recent-items-container" style="display: ${this._baseMenu.searchTerm ? 'none' : 'block'}">
          <div class="recent-items">
            ${this.createRecentItemsHTML()}
          </div>
        </div>       
        <div class="menu-content">
          ${this._baseMenu.isSearchMode
        ? `<ul class="menu search-results">${this.createSearchResults(this._baseMenu.searchResults)}</ul>`
        : this.createMenuTree(this._baseMenu.menuData)}
        </div>
      </div>
    `;

    // Add click event to menu button
    const menuButton = this.shadowRoot.querySelector(".menu-button") as HTMLButtonElement | null;
    if (menuButton) {
      menuButton.addEventListener("click", (e) => this.toggleMenu(e));
    }

    // Add input event to search box
    const searchInput = this.shadowRoot.querySelector(".search-input") as HTMLInputElement | null;
    if (searchInput) {
      searchInput.addEventListener("input", (e) => this.handleSearch(e));
    }

    // Add event listeners to menu items
    this.attachMenuEventListeners();
    void adoptPrimeIconsStyles(this.shadowRoot);
    hydrateQdsLucideIcons(this.shadowRoot);

    // Log the generated markup in debug mode
    if (this.hasAttribute('debug')) {
      setTimeout(() => {
        const menuContainerHTML = this.shadowRoot?.querySelector('.menu-container')?.innerHTML;
        console.log("Generated menu HTML:", menuContainerHTML);
      }, 100);
    }
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get("qds-hierarchical-menu")) {
  customElements.define("qds-hierarchical-menu", QdsHierarchicalMenu);
}

export { QdsHierarchicalMenu };
